export const getColorForAssetType = (assetType) => {
  switch(assetType) {
    case 'Equity':
      return "#008ed1";
    case 'Bond':
      return "#00d0af";
    case 'Real Estate':
      return "#9786ec";
    case 'Multi Asset':
      return "#414bdf";
    case 'Commodity':
      return "#80c6e8";
    case 'Gold':
      return "#80e7d7";
    default:
      return "#80e7d7";  // Default color for unknown asset types
  }
};

export const assetTypeColors = {
  Equity: "red",
  "Real Estate": "cyan",
  Bond: "blue",
  "Multi Asset": "green",
  Commodity: "black",
  Gold: "yellow",
  Unknown: "gray",
  "Fixed Income": "purple",
  "Cash": "orange",
  "Alternative": "pink",
  "Infrastructure": "brown",
  "Private Equity": "indigo",
  "Hedge Fund": "teal",
  "REIT": "lime",
  "ETF": "magenta",
  "Mutual Fund": "navy",
  "Derivative": "olive"
};

// Simple hash function to create deterministic randomness from a string
const simpleHash = (str) => {
  let hash = 0;
  if (!str || str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

// Deterministic shuffle function using a seed
const deterministicShuffle = (array, seed) => {
  const shuffled = [...array];
  let currentSeed = seed;

  for (let i = shuffled.length - 1; i > 0; i--) {
    // Generate deterministic "random" number
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    const randomValue = currentSeed / 233280;
    const j = Math.floor(randomValue * (i + 1));

    // Swap elements
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};

// Function to generate deterministic colors for assets based on strategy ID
export const getDeterministicColors = (numberOfAssets, strategyId = 'default') => {
  // Get all available colors except gray
  const availableColors = Object.values(assetTypeColors).filter(color => color !== "gray");

  // Create a hash from the strategy ID
  const seed = simpleHash(strategyId.toString());

  // If we have fewer assets than available colors, shuffle and take what we need
  if (numberOfAssets <= availableColors.length) {
    const shuffled = deterministicShuffle(availableColors, seed);
    return shuffled.slice(0, numberOfAssets);
  }

  // If we have more assets than colors, repeat colors deterministically
  const colors = [];
  let currentSeed = seed;

  for (let i = 0; i < numberOfAssets; i++) {
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    const randomValue = currentSeed / 233280;
    const colorIndex = Math.floor(randomValue * availableColors.length);
    colors.push(availableColors[colorIndex]);
  }

  return colors;
};

// Function to get deterministic colors for strategy overview
export const getDeterministicAssetColors = (assets, strategyId) => {
  if (!assets || assets.length === 0) return [];

  return getDeterministicColors(assets.length, strategyId);
};

// Function to aggregate daily data to monthly data
export const aggregateToMonthly = (data, valueField = 'value', dateField = 'date') => {
  if (!data || data.length === 0) return [];

  // Group data by year-month
  const monthlyGroups = {};

  data.forEach(item => {
    const date = new Date(item[dateField]);
    const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

    if (!monthlyGroups[yearMonth]) {
      monthlyGroups[yearMonth] = {
        dates: [],
        values: [],
        yearMonth: yearMonth,
        year: date.getFullYear(),
        month: date.getMonth()
      };
    }

    monthlyGroups[yearMonth].dates.push(item[dateField]);
    monthlyGroups[yearMonth].values.push(item[valueField]);
  });

  // Convert to monthly data points (use last value of each month)
  return Object.keys(monthlyGroups)
    .sort()
    .map(yearMonth => {
      const group = monthlyGroups[yearMonth];
      const lastIndex = group.values.length - 1;

      return {
        [dateField]: group.dates[lastIndex], // Last date of the month
        [valueField]: group.values[lastIndex], // Last value of the month
        month: getMonthName(group.month),
        year: group.year,
        monthYear: `${getMonthName(group.month)} ${group.year}`
      };
    });
};

// Helper function to get month names
export const getMonthName = (monthIndex) => {
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  return months[monthIndex];
};

// Smart data sampling for large datasets - TEMPORARILY DISABLED FOR TESTING
export const smartDownsample = (data, maxPoints = 1000) => {
  // TESTING: Always return original data (downsampling disabled)
  return data || [];

  /* ORIGINAL CODE (commented out for testing):
  if (!data || data.length <= maxPoints) {
    return data;
  }

  const step = Math.ceil(data.length / maxPoints);
  const sampledData = [];

  // Always include first and last points
  sampledData.push(data[0]);

  // Sample intermediate points
  for (let i = step; i < data.length - 1; i += step) {
    sampledData.push(data[i]);
  }

  // Always include last point
  if (data.length > 1) {
    sampledData.push(data[data.length - 1]);
  }

  return sampledData;
  */
};

// Enhanced function to format performance data with adaptive aggregation
export const formatPerformanceDataForDisplay = (performanceData, forceDaily = false, maxDailyPoints = 999999) => {
  if (!performanceData || !performanceData.performance_data) {
    return null;
  }

  const rawData = performanceData.performance_data;

  // Decide whether to use daily or monthly data based on dataset size and user preference
  const shouldUseDaily = forceDaily || rawData.length <= maxDailyPoints;

  if (shouldUseDaily) {
    // Use daily data (with smart downsampling if needed)
    const processedData = smartDownsample(rawData, maxDailyPoints);

    return {
      labels: processedData.map(item => {
        const date = new Date(item.date);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: rawData.length > 365 ? 'numeric' : undefined // Only show year if data spans more than a year
        });
      }),
      datasets: [
        {
          label: 'Strategy Performance',
          data: processedData.map(item => item.performance),
          borderColor: 'rgba(75,192,192,1)',
          backgroundColor: 'rgba(75,192,192,0.4)',
          pointRadius: rawData.length > 365 ? 0 : 1, // Hide points for large datasets
          pointHoverRadius: 3,
        },
      ],
      isDaily: true,
      originalDataLength: rawData.length,
      displayDataLength: processedData.length
    };
  } else {
    // Use monthly aggregated data for very large datasets
    const monthlyData = aggregateToMonthly(rawData, 'performance', 'date');

    return {
      labels: monthlyData.map(item => item.monthYear),
      datasets: [
        {
          label: 'Strategy Performance',
          data: monthlyData.map(item => item.performance),
          borderColor: 'rgba(75,192,192,1)',
          backgroundColor: 'rgba(75,192,192,0.4)',
          pointRadius: 2,
          pointHoverRadius: 4,
        },
      ],
      isDaily: false,
      originalDataLength: rawData.length,
      displayDataLength: monthlyData.length
    };
  }
};

// Legacy function to maintain backward compatibility
export const formatPerformanceDataForMonthly = (performanceData) => {
  const result = formatPerformanceDataForDisplay(performanceData, false);
  return result ? {
    labels: result.labels,
    datasets: result.datasets
  } : null;
};

// Enhanced function to format drawdown data with adaptive aggregation
export const formatDrawdownDataForDisplay = (drawdownData, forceDaily = false, maxDailyPoints = 999999) => {
  if (!drawdownData || drawdownData.length === 0) {
    return [];
  }

  // Decide whether to use daily or monthly data based on dataset size and user preference
  const shouldUseDaily = forceDaily || drawdownData.length <= maxDailyPoints;

  if (shouldUseDaily) {
    // Use daily data (with smart downsampling if needed)
    return smartDownsample(drawdownData, maxDailyPoints);
  } else {
    // Use monthly aggregated data for very large datasets
    return aggregateToMonthly(drawdownData, 'drawdown', 'date');
  }
};

// Legacy function to maintain backward compatibility
export const formatDrawdownDataForMonthly = (drawdownData) => {
  return formatDrawdownDataForDisplay(drawdownData, false);
};
